import * as React from "react"
import {
  LayoutDashboard,
  Users,
  Calendar,
  CheckSquare,
  ContactRound,
  Building2,
  Mail,
  DollarSign,
  BarChart3,
  FileBarChart,
  FileText,
  Receipt,
  Workflow,
  Briefcase,
  Inbox,
} from "lucide-react"

import { NavMain } from "@/components/layout/nav-main"
// import { NavProjects } from "@/components/layout/nav-projects"
import { AppLogo } from "@/components/layout/app-logo"
import { TenantMenu } from "@/components/layout/tenant-menu"
import { SidebarSearch } from "@/components/layout/sidebar-search"
import { SidebarSupportChat } from "@/components/support-chat/SidebarSupportChat"
import { NotificationFeed } from "@/components/notifications/notification-feed"
import { TaskFeed } from "@/components/tasks/task-feed"
import { InboxNavBadge } from "@/components/layout/inbox-nav-badge"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Kbd } from "@/components/ui/kbd"
import {
  useHasEmailInboxFeature,
  useHasJobsFeature,
} from "@/hooks/use-has-feature"
import { usePermissions } from "@/hooks/use-permissions"
import { getVisibleSettingsNavItems } from "@/lib/settings-nav"
import { useStore } from "@tanstack/react-store"
import { organizationStore } from "@/stores/organization.store"
import { useStripeConnection } from "@/stores/stripe.store"

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { hasFeature: hasJobs } = useHasJobsFeature()
  const { hasFeature: hasEmailInbox } = useHasEmailInboxFeature()
  const selectedOrganizationId = useStore(organizationStore, (s) => s.selectedOrganizationId)
  const { isConnected: isStripeConnected } = useStripeConnection(selectedOrganizationId || '')
  const { can, isEventStaff } = usePermissions(selectedOrganizationId || '')
  const { state, isMobile } = useSidebar()

  // Each entry is gated on the permission its destination's *primary* read
  // requires per the backend policy registry
  // (backend/internal/api/middleware/rbacpolicy/policy_definitions.go), so an
  // item only appears when the page behind it can actually load. Pages whose
  // primary read is member-level (Dashboard, Tasks, Attachment Library) and the
  // Marketing placeholder carry no gate at all. Destinations with two
  // independent entry permissions OR them, as Company already did.
  const canViewLeads = can('LeadsView')
  const canViewEvents = can('EventsView')
  const canViewQuotes = can('QuotesView')
  const canViewInvoices = can('InvoicesView')
  const canViewAutomations = can('AutomationsView')
  const canViewContacts = can('ContactsView')
  const canViewReporting = can('ReportingView')
  const canViewComms = can('CommsView')

  const navDashboard = [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: LayoutDashboard,
    },
    // The Inbox lists every connected mailbox's threads (ListEmailThreads is
    // CommsView) and only exists with the Connected inbox add-on.
    ...(hasEmailInbox && canViewComms ? [{
      title: "Inbox",
      url: "/inbox",
      icon: Inbox,
      badge: <InboxNavBadge />,
    }] : []),
    // The calendar overlays events and leads (tasks are member-level), so
    // either domain's view permission makes it renderable.
    ...(canViewEvents || canViewLeads ? [{
      title: "Calendar",
      url: "/calendar",
      icon: Calendar,
    }] : []),
    ...(canViewLeads ? [{
      title: "Leads",
      url: "/leads",
      icon: Users,
    }] : []),
    // ListJobs is authorized with LeadsView, so the entry follows that permission.
    ...(hasJobs && canViewLeads ? [{
      title: "Pipeline",
      url: "/pipeline",
      icon: Briefcase,
    }] : []),
    ...(canViewEvents ? [{
      title: "Events",
      url: "/events",
      icon: Calendar,
    }] : []),
    ...(canViewQuotes ? [{
      title: "Quotes",
      url: "/quotes",
      icon: FileText,
    }] : []),
    ...(isStripeConnected && canViewInvoices ? [{
      title: "Invoices",
      url: "/invoices",
      icon: DollarSign,
    }] : []),
    {
      title: "Tasks",
      url: "/tasks",
      icon: CheckSquare,
    },
    ...(canViewAutomations ? [{
      title: "Workflows",
      url: "/workflows",
      icon: Workflow,
    }] : []),
  ]

  const navCRM = [
    ...(canViewContacts ? [{
      title: "Contacts",
      url: "/contacts",
      icon: ContactRound,
    }] : []),
    ...(canViewContacts ? [{
      title: "Companies",
      url: "/companies",
      icon: Building2,
    }] : []),
    {
      title: "Marketing",
      url: "/marketing",
      icon: Mail,
    },
  ]

  const navReporting = canViewReporting ? [
    {
      title: "Overview",
      url: "/reports",
      icon: BarChart3,
    },
    {
      title: "Revenue",
      url: "/revenue",
      icon: FileBarChart,
    },
    {
      title: "Sales Tax",
      url: "/sales-tax",
      icon: Receipt,
    },
  ] : []

  const navSettings = getVisibleSettingsNavItems({ can }).map(
    ({ title, url, icon, badge }) => ({ title, url, icon, badge })
  )

  // Event Staff hold no RBAC roles, so `can()` denies everything above.
  // Their access is a fixed, non-RBAC scope instead: Calendar, Events, and
  // Contacts only (Catalog is reached from within event views, not its own
  // nav entry) — no Dashboard, no Settings, no AI agent.
  const eventStaffNav = [
    { title: "Calendar", url: "/calendar", icon: Calendar },
    { title: "Events", url: "/events", icon: Calendar },
    { title: "Contacts", url: "/contacts", icon: ContactRound },
  ]

  // The sidebar is a dark surface (Shopify-admin style) even in light mode;
  // the inset variant keeps the wrapper behind the floating content card on
  // the light --background, so only the card and the sidebar read as distinct
  // surfaces.
  return (
    <Sidebar collapsible="icon" variant="inset" {...props}>
      <SidebarHeader>
        {/* Logo left, collapse toggle right; stacked in the icon rail. */}
        <div className="flex items-center gap-1 group-data-[collapsible=icon]:flex-col">
          <div className="min-w-0 flex-1 group-data-[collapsible=icon]:flex-none">
            <AppLogo />
          </div>
          <Tooltip delayDuration={800}>
            <TooltipTrigger asChild>
              <SidebarTrigger
                aria-label={state === "collapsed" ? "Expand sidebar" : "Collapse sidebar"}
                className="size-8 shrink-0 rounded-lg text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              />
            </TooltipTrigger>
            <TooltipContent side="right">
              <Kbd>⌘B</Kbd>
            </TooltipContent>
          </Tooltip>
        </div>
        <SidebarSearch />
      </SidebarHeader>
      <SidebarContent>
        {isEventStaff ? (
          <NavMain items={eventStaffNav} label="Home" />
        ) : (
          <>
            {/* A group whose every item was permission-filtered away would render as
                a bare, empty heading — drop the group instead. */}
            {navDashboard.length > 0 && <NavMain items={navDashboard} label="Home" collapsible />}
            {navCRM.length > 0 && <NavMain items={navCRM} label="CRM" collapsible />}
            {navReporting.length > 0 && <NavMain items={navReporting} label="Reporting" collapsible />}
            {navSettings.length > 0 && <NavMain items={navSettings} label="Settings" collapsible />}
          </>
        )}
      </SidebarContent>
      <SidebarSeparator />
      <SidebarFooter>
        <SidebarSupportChat />
        {/* Tenant row: org + user menu on the left, feeds on the right. In the
            icon rail the feeds stack above the org avatar. On mobile the feeds
            move to the content card's top bar instead, because their popovers
            can't scroll inside the sidebar sheet. */}
        <div className="flex items-center gap-1 group-data-[collapsible=icon]:flex-col-reverse">
          <div className="min-w-0 flex-1">
            <TenantMenu />
          </div>
          {!isMobile && (
            <>
              <TaskFeed side="right" className="size-8 shrink-0 rounded-lg" />
              <NotificationFeed side="right" className="size-8 shrink-0 rounded-lg" />
            </>
          )}
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
